/**
 * Dashboard API
 * 대시보드 요약 및 주간 데이터 조회 API
 *
 * @description
 * - 대시보드 요약 조회 (1분 주기 자동 갱신)
 * - 주간 슬롯/물량/라이더 현황 조회
 */
import { apiClient } from "@/shared/api/client";
import { formatDateToISO, removeDateHyphens } from "@/shared/utils/dateUtils";
import type {
    DashboardSummaryResponse,
    DashboardWeeklyRequestParams,
    DashboardWeeklyResponse,
} from "../types/dashboard.types";

// 타입 re-export
export type {
    DashboardStatusSummaryItem,
    DashboardSummaryResponse,
    DashboardWeeklyRequestParams,
    DashboardWeeklyDailyPerformance,
    DashboardWeeklySupplierPerformance,
    DashboardWeeklyResponse,
} from "../types/dashboard.types";

// ============================================================
// 상수 정의
// ============================================================

/** 대시보드 API 기본 경로 */
const BASE_PATH = "/api/v1/dashboard";

// ============================================================
// 헬퍼 함수
// ============================================================

/**
 * 주간 조회용 쿼리 파라미터 생성
 *
 * @param params 요청 파라미터
 * @returns URLSearchParams 객체
 */
const buildWeeklyQueryParams = (params: DashboardWeeklyRequestParams): URLSearchParams => {
    const formattedFromDate = removeDateHyphens(formatDateToISO(params.fromDate));
    const formattedToDate = removeDateHyphens(formatDateToISO(params.toDate));

    const query = new URLSearchParams({
        fromDate: formattedFromDate,
        toDate: formattedToDate,
    });

    if (params.suppId) {
        query.append('suppId', params.suppId);
    }

    return query;
};

// ============================================================
// API 서비스
// ============================================================

/**
 * 대시보드 API 서비스
 */
export const dashboardApi = {
    /**
    * 대시보드 주간 요약 조회
    * GET /api/v1/dashboard/weekly
    *
    * @description
    * 상단 카드/요약 영역에서 사용 (1분 주기 자동 갱신)
    *
    * @param params 사용자 정보 (userType, userId)
    * @returns 대시보드 요약 응답
    */
    getSummary: async (params?: { userType?: string; userId?: string; }): Promise<DashboardSummaryResponse> => {
        const response = await apiClient.get<DashboardSummaryResponse>(
        `${BASE_PATH}/weekly`,
            { params },
        );
        return response.data;
    },

    /**
     * 주 단위 슬롯/물량/라이더 현황 조회
     * GET /api/v1/dashboard/weekly?fromDate=YYYYMMDD&toDate=YYYYMMDD&suppId={id}
     *
     * @params params 조회 파라미터 (fromDate, toDate, suppId)
     * @returns 주간 현황 응답
     */
    getWeekly: async (params: DashboardWeeklyRequestParams): Promise<DashboardWeeklyResponse> => {
        const query = buildWeeklyQueryParams(params);
        const response = await apiClient.get<DashboardWeeklyResponse>(
        `${BASE_PATH}/weekly?${query.toString()}`,
        );
        return response.data;
    },
};
